import React, { useState, useEffect, useCallback } from 'react';
import { stripeConnectAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CreditCard, ExternalLink, CheckCircle2, XCircle, AlertCircle, Building2, Banknote } from 'lucide-react';
import { toast } from 'sonner';

export function StripeConnectSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await stripeConnectAPI.status();
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const origin = window.location.origin;
      const res = await stripeConnectAPI.onboard({
        return_url: `${origin}/company-settings?tab=payments&stripe=return`,
        refresh_url: `${origin}/company-settings?tab=payments&stripe=refresh`,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start Stripe onboarding');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Stripe account? Customers will no longer be able to pay invoices online.')) return;
    setDisconnecting(true);
    try {
      await stripeConnectAPI.disconnect();
      toast.success('Stripe account disconnected');
      fetchStatus();
    } catch {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const connected = status?.connected;
  const chargesEnabled = status?.charges_enabled;
  const detailsSubmitted = status?.details_submitted;
  const payoutsEnabled = status?.payouts_enabled;

  return (
    <div className="space-y-6" data-testid="payments-settings">
      <Card data-testid="stripe-connect-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            <CardTitle>Payments</CardTitle>
          </div>
          <CardDescription>
            Accept online payments on invoices. Funds go directly to your bank account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {!connected ? (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/30 p-4 space-y-2">
                <p className="text-sm font-medium">Why connect with Stripe?</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Accept credit card, ACH, Apple Pay, and Google Pay on invoices</li>
                  <li>Funds deposited directly to your bank account</li>
                  <li>Stripe handles all payment security and PCI compliance</li>
                  <li>Customers see a professional checkout experience</li>
                  <li>0.50% platform fee per transaction (Stripe processing fees apply separately)</li>
                </ul>
              </div>

              {/* Pre-onboarding expectations */}
              <div className="rounded-md border border-amber-200 bg-amber-50/50 dark:bg-amber-950/10 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">What to expect</p>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  You'll be redirected to Stripe for a quick secure setup. We've pre-filled your business info to speed things up. Stripe may ask for:
                </p>
                <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5 list-disc list-inside pl-1">
                  <li>Business details confirmation</li>
                  <li>Identity verification (name, SSN or EIN)</li>
                  <li>Bank account for receiving payouts</li>
                </ul>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  You can start accepting payments right away — additional details can be completed later as needed.
                </p>
              </div>

              <Button onClick={handleConnect} disabled={connecting} size="lg" data-testid="connect-stripe-btn">
                {connecting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirecting to Stripe...</>
                ) : (
                  <><CreditCard className="w-4 h-4 mr-2" /> Connect Stripe for Payouts</>
                )}
              </Button>
              <p className="text-[11px] text-muted-foreground">
                You'll be securely redirected to Stripe to complete setup. No payment info is stored in Stock &amp; Load.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Status row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg border p-3.5 space-y-1" data-testid="payment-status-card">
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">Status</p>
                  <div className="flex items-center gap-2">
                    {chargesEnabled ? (
                      <Badge className="bg-green-100 text-green-700 gap-1" data-testid="stripe-status-badge">
                        <CheckCircle2 className="w-3 h-3" /> Connected
                      </Badge>
                    ) : detailsSubmitted ? (
                      <Badge className="bg-amber-100 text-amber-700 gap-1" data-testid="stripe-status-badge">
                        <AlertCircle className="w-3 h-3" /> Pending
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 gap-1" data-testid="stripe-status-badge">
                        <AlertCircle className="w-3 h-3" /> Incomplete
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border p-3.5 space-y-1" data-testid="payout-account-card">
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">Account</p>
                  <p className="text-sm font-mono text-foreground">{status.account_id}</p>
                </div>

                <div className="rounded-lg border p-3.5 space-y-1" data-testid="payout-status-card">
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">Payouts</p>
                  <div className="flex items-center gap-1.5">
                    {payoutsEnabled ? (
                      <>
                        <Banknote className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">Enabled</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-400" />
                        <span className="text-sm font-medium text-muted-foreground">Disabled</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Charges + Payouts detail */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  {chargesEnabled ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}
                  <span className={chargesEnabled ? 'text-foreground' : 'text-muted-foreground'}>Card & ACH payments</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {payoutsEnabled ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}
                  <span className={payoutsEnabled ? 'text-foreground' : 'text-muted-foreground'}>Bank payouts</span>
                </div>
              </div>

              {/* Onboarding incomplete banner */}
              {!chargesEnabled && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-3">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {detailsSubmitted
                      ? 'Your account is under review by Stripe. This usually takes 1-2 business days.'
                      : 'Your Stripe payout setup is incomplete. Please finish the remaining steps to start accepting payments.'}
                  </p>
                  {!detailsSubmitted && (
                    <Button size="sm" variant="outline" className="mt-2" onClick={handleConnect} disabled={connecting}>
                      {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                      Finish Stripe Payout Setup
                    </Button>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                {status.dashboard_url && (
                  <Button variant="outline" size="sm" asChild data-testid="stripe-dashboard-btn">
                    <a href={status.dashboard_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {status.account_type === 'express' ? 'Payouts Dashboard' : 'Open Stripe Dashboard'}
                    </a>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  data-testid="disconnect-stripe-btn"
                >
                  {disconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                  Disconnect
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment methods info */}
      {connected && chargesEnabled && (
        <Card data-testid="payment-methods-info">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Accepted Payment Methods</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-2 rounded-md border p-2.5">
                <CreditCard className="w-4 h-4 text-blue-600" />
                <span>Credit / Debit Cards</span>
              </div>
              <div className="flex items-center gap-2 rounded-md border p-2.5">
                <Building2 className="w-4 h-4 text-green-600" />
                <span>ACH Bank Transfer</span>
              </div>
              <div className="flex items-center gap-2 rounded-md border p-2.5">
                <span className="text-base">&#xF8FF;</span>
                <span>Apple Pay</span>
              </div>
              <div className="flex items-center gap-2 rounded-md border p-2.5">
                <span className="font-bold text-sm text-blue-500">G</span>
                <span>Google Pay</span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              Apple Pay and Google Pay are automatically available when card payments are enabled.
              A 0.50% platform fee applies per transaction. Stripe processing fees apply separately.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
